<template>
  <v-card dark flat class="px-5 mt-5">
    <v-card-title class="font-weight-bold text-h4 mb-6 px-0"> Studio & Mitarbeiter Übersicht </v-card-title>

    <!-- 1. STUDIO TABELLE -->
    <v-card color="transparent" flat class="mb-10">
      <v-card-title class="px-0 text-h6 font-weight-bold">
        Studio Kennzahlen & Auslastung
        <v-spacer></v-spacer>
        <!-- Suchfeld für Studios -->
        <v-text-field
          v-model="searchStudio"
          append-icon="mdi-magnify"
          label="Studios durchsuchen..."
          single-line
          hide-details
          outlined
          dense
          style="max-width: 300px"></v-text-field>
      </v-card-title>

      <v-data-table
        :headers="studioHeaders"
        :items="studioTableData"
        :search="searchStudio"
        class="elevation-1"
        :items-per-page="10"
        sort-by="customersPerFTE"
        sort-desc>
        <!-- Custom Rendering für die Stellenprozente -->
        <template v-slot:item.totalPercentage="{ item }">
          <span class="font-weight-bold">{{ item.totalPercentage }} %</span>
        </template>

        <!-- Custom Rendering für Betreuungs-Index -->
        <template v-slot:item.customersPerFTE="{ item }">
          <span :class="{ 'red--text font-weight-bold': item.isAboveAverage, 'green--text font-weight-bold': !item.isAboveAverage }">
            {{ item.customersPerFTE }}
          </span>
        </template>

        <!-- Custom Rendering für Auslastungs-Balken -->
        <template v-slot:item.workloadScore="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" style="min-width: 120px; display: flex; align-items: center">
                <v-progress-linear
                  :value="item.workloadScore"
                  :color="item.isAboveAverage ? 'error' : 'success'"
                  height="12"
                  rounded></v-progress-linear>
              </div>
            </template>
            <span>
              Ø Firmen-Schnitt: {{ item.avgCustomersPerFTE }} Kunden pro 100% MA<br />
              Dieses Studio: {{ item.customersPerFTE }}
            </span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <v-divider class="mb-10"></v-divider>

    <!-- 2. MITARBEITER TABELLE -->
    <v-card color="transparent" flat class="mb-12">
      <v-card-title class="px-0 text-h6 font-weight-bold">
        Alle Mitarbeiter
        <v-spacer></v-spacer>
        <!-- Filter-Dropdown für Studios -->
        <v-select
          v-model="selectedStudio"
          :items="studioOptions"
          label="Nach Studio filtern"
          outlined
          dense
          hide-details
          class="mr-4"
          style="max-width: 250px"></v-select>
        <!-- Suchfeld für Mitarbeiter -->
        <v-text-field
          v-model="searchEmployee"
          append-icon="mdi-magnify"
          label="Mitarbeiter suchen..."
          single-line
          hide-details
          outlined
          dense
          style="max-width: 300px"></v-text-field>
      </v-card-title>

      <v-data-table
        :headers="employeeHeaders"
        :items="filteredEmployeeTableData"
        :search="searchEmployee"
        class="elevation-1"
        :items-per-page="15"
        sort-by="empName">
        <!-- Custom Rendering für die Stellenprozente -->
        <template v-slot:item.empPercentage="{ item }">
          <v-chip color="secondary" small dark> {{ item.empPercentage }} % </v-chip>
        </template>
        <!-- Custom Rendering für das Studio Tag -->
        <template v-slot:item.studioName="{ item }">
          <v-chip outlined small>
            {{ item.studioName }}
          </v-chip>
        </template>
      </v-data-table>
    </v-card>
  </v-card>
</template>

<script>
import api from "@/api"

export default {
  data() {
    return {
      employeeStats: [],

      // Suche & Filter
      searchStudio: "",
      searchEmployee: "",
      selectedStudio: "Alle",

      // Tabellen-Köpfe (Headers)
      studioHeaders: [
        { text: "Studio", value: "name", align: "start" },
        { text: "Quadratmeter", value: "squareMeter", align: "end" },
        { text: "Anzahl Kunden", value: "customerAmount", align: "end" },
        { text: "Anzahl MA", value: "employeeCount", align: "end" },
        { text: "Stellenprozente Total", value: "totalPercentage", align: "end" },
        { text: "Kunden pro 100% MA", value: "customersPerFTE", align: "end" },
        { text: "Relative Auslastung", value: "workloadScore", align: "center", sortable: false },
      ],
      employeeHeaders: [
        //  { text: "Mitarbeiter/in", value: "empName", align: "start" },
        { text: "Funktion / Titel", value: "empTitle", align: "start" },
        { text: "Studio", value: "studioName", align: "start" },
        { text: "Stellenprozent", value: "empPercentage", align: "end" },
      ],
    }
  },
  computed: {
    // 1. Rechnet die Backend-Daten in eine flache Liste für die Studio-Tabelle um
    studioTableData() {
      if (!this.employeeStats.length) return []

      let studios = this.employeeStats.map((stat) => {
        const percentages = stat.percentage || []
        const totalPercentage = percentages.reduce((sum, item) => sum + (item.percentage || 0), 0)

        // Berechnung: Vollzeitäquivalente (FTE)
        const fte = totalPercentage / 100

        // Kennzahl: Wie viele Kunden kommen auf eine 100% Stelle?
        const customersPerFTE = fte > 0 ? Math.round((stat.customerAmount || 0) / fte) : 0

        return {
          name: stat.name,
          squareMeter: stat.squareMeter || 0,
          customerAmount: stat.customerAmount || 0,
          employeeCount: percentages.length,
          totalPercentage: totalPercentage,
          customersPerFTE: customersPerFTE,
        }
      })

      // Vergleichsdaten über ALLE Studios berechnen
      const maxCustomersPerFTE = Math.max(...studios.map((s) => s.customersPerFTE), 1) // Verhindert Division durch 0
      const totalFteAcrossStudios = studios.reduce((sum, s) => sum + s.customersPerFTE, 0)
      const avgCustomersPerFTE = Math.round(totalFteAcrossStudios / (studios.length || 1))

      // Werte für die Darstellung (Balken & Farben) anhängen
      return studios.map((s) => {
        // Prozentualer Score im Verhältnis zum "am stärksten ausgelasteten" Studio (0-100%)
        s.workloadScore = Math.round((s.customersPerFTE / maxCustomersPerFTE) * 100)

        // Ist dieses Studio stärker belastet als der Firmen-Schnitt?
        s.isAboveAverage = s.customersPerFTE > avgCustomersPerFTE
        s.avgCustomersPerFTE = avgCustomersPerFTE

        return s
      })
    },

    // 2. Extrahiert ALLE Mitarbeiter (Fix für fullName)
    employeeTableData() {
      let allEmployees = []
      this.employeeStats.forEach((stat) => {
        if (stat.percentage && Array.isArray(stat.percentage)) {
          stat.percentage.forEach((emp) => {
            allEmployees.push({
              // HIER DER FIX: Nimmt fullName, falls vorhanden, sonst name
              empName: emp.fullName || emp.name || "Name unbekannt",
              empTitle: emp.title || "-",
              empPercentage: emp.percentage || 0,
              studioName: stat.name,
            })
          })
        }
      })
      return allEmployees
    },

    // 3. Generiert die Dropdown-Optionen für den Filter (immer "Alle" als erstes)
    studioOptions() {
      const studios = this.employeeStats.map((stat) => stat.name)
      return ["Alle", ...studios]
    },

    // 4. Wendet den Studio-Filter auf die Mitarbeiter-Liste an
    filteredEmployeeTableData() {
      if (this.selectedStudio === "Alle") {
        return this.employeeTableData
      }
      return this.employeeTableData.filter((emp) => emp.studioName === this.selectedStudio)
    },
  },
  async created() {
    this.init()
  },
  methods: {
    async init() {
      try {
        let infos = await api.fetchEmployeeStatistics()
        this.employeeStats = infos.employeeStatistic || []
      } catch (e) {
        console.error("Fehler beim Laden der Statistiken:", e)
      }
    },
  },
}
</script>
